import React, { useRef, useState } from "react";
import ContentButton from "../core/ui/ContentButton";
import {
  buildLeadIntakePayload,
  LeadFormValues,
  LeadPageSlug,
  submitLeadIntake,
} from "@webcore/services/leadIntake/leadIntake.ts";
import { trackLeadFormSubmit } from "@webcore/services/telemetry/leadConversion.ts";

interface LeadFormSectionProps {
  // Explicit per placement — see LeadPageSlug.
  pageSlug: LeadPageSlug;
  // Shown inside the success state so the lead is captured before any calendar step.
  calendarHref: string;
}

type SubmitStatus = "idle" | "submitting" | "success" | "error";

function readFormValues(form: HTMLFormElement): LeadFormValues {
  const data = new FormData(form);
  const field = (name: keyof LeadFormValues): string => String(data.get(name) ?? "");
  return {
    first_name: field("first_name"),
    last_name: field("last_name"),
    email: field("email"),
    company_name: field("company_name"),
    job_title: field("job_title"),
    pain: field("pain"),
    website: field("website"),
  };
}

/**
 * The "book a walkthrough" lead-capture card (D-72). Markup and field names are ported from the
 * Vercel landing page's form and are validated by the intake endpoint — keep every `name`
 * attribute and the hidden `website` honeypot exactly as they are.
 */
const LeadFormSection: React.FC<LeadFormSectionProps> = ({ pageSlug, calendarHref }) => {
  const formRef = useRef<HTMLFormElement>(null);
  const [status, setStatus] = useState<SubmitStatus>("idle");

  const leadForm_submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = formRef.current;
    if (form === null || status === "submitting") {
      return;
    }
    if (!form.reportValidity()) {
      return;
    }

    setStatus("submitting");
    const payload = buildLeadIntakePayload(readFormValues(form), pageSlug);
    try {
      await submitLeadIntake(payload);
    } catch {
      // Non-200 or network failure: a public visitor gets the inline note, not the error boundary.
      setStatus("error");
      return;
    }

    setStatus("success");
    trackLeadFormSubmit({ page_slug: payload.page_slug, utm_source: payload.utm_source });
  };

  if (status === "success") {
    return (
      <section className="cs-form-card" data-testid="lead-form-success">
        <div className="cs-form-card-success">
          <div className="cs-form-card-tick">// request received</div>
          <h2>You are on the list.</h2>
          <p>
            Watch your inbox. We reply within one business day with a walkthrough matched to your
            stack, and we bring your reconciliation question with us.
          </p>
          <p>
            Already know when you want to talk?{" "}
            <a href={calendarHref} target="_blank" rel="noopener noreferrer">Pick a time on our calendar →</a>
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="cs-form-card" data-testid="lead-form-card">
      <form ref={formRef} noValidate onSubmit={leadForm_submit} data-testid="lead-form">
        <div className="eyebrow">// book a walkthrough</div>
        <h2>See your numbers reconcile.</h2>
        <p className="cs-form-card-sub">
          Tell us where reporting breaks today. We reply within one business day with a walkthrough
          matched to your stack.
        </p>

        <div className="cs-form-card-row">
          <div className="cs-field">
            <label className="cs-field-label" htmlFor="lead-first-name">First name</label>
            <input className="cs-input" id="lead-first-name" name="first_name" autoComplete="given-name" maxLength={80} required />
          </div>
          <div className="cs-field">
            <label className="cs-field-label" htmlFor="lead-last-name">Last name</label>
            <input className="cs-input" id="lead-last-name" name="last_name" autoComplete="family-name" maxLength={80} required />
          </div>
        </div>

        <div className="cs-field">
          <label className="cs-field-label" htmlFor="lead-email">Work email</label>
          <input className="cs-input" id="lead-email" name="email" type="email" autoComplete="email" placeholder="you@company.com" required />
        </div>

        <div className="cs-form-card-row">
          <div className="cs-field">
            <label className="cs-field-label" htmlFor="lead-company-name">Company</label>
            <input className="cs-input" id="lead-company-name" name="company_name" autoComplete="organization" maxLength={120} required />
          </div>
          <div className="cs-field">
            <label className="cs-field-label" htmlFor="lead-job-title">Job title</label>
            <input className="cs-input" id="lead-job-title" name="job_title" autoComplete="organization-title" maxLength={120} />
          </div>
        </div>

        <div className="cs-field">
          <label className="cs-field-label" htmlFor="lead-pain">
            What are you trying to reconcile? <span className="cs-field-label-optional">(optional)</span>
          </label>
          <textarea
            className="cs-textarea"
            id="lead-pain"
            name="pain"
            maxLength={1000}
            placeholder="e.g. ARR differs between HubSpot and NetSuite every board cycle"
          />
        </div>

        <div className="cs-form-card-honeypot" aria-hidden="true">
          <label className="cs-field-label" htmlFor="lead-website">Website</label>
          <input className="cs-input" id="lead-website" name="website" tabIndex={-1} autoComplete="off" />
        </div>

        <ContentButton type="submit" variant="primary" size="lg" className="cs-form-card-submit" disabled={status === "submitting"}>
          {status === "submitting" ? "Sending…" : "Book a walkthrough →"}
        </ContentButton>
        <p className="cs-form-card-fine">One reply from a human, plus one follow up if we miss you.</p>
        {status === "error" && (
          <p className="cs-form-card-error" data-testid="lead-form-error">
            Something went wrong sending your request. Please try again, or email{" "}
            <a href="mailto:hello@datafabriq.co">hello@datafabriq.co</a>.
          </p>
        )}
      </form>
    </section>
  );
};

export default LeadFormSection;
